import { useEffect, useState } from "react";
import { Config } from "../config/Config";
import { fetchWithAuth } from "./fetchWithAuth";
import { oauthClient } from "../apis/OAuthClient";

// Performance-Ampel (Masterplan Paket 7): generisch aufgebaut. Jede Quelle ist ein Provider, der
// ein Signal liefert (oder null = "weiss nichts"). Die Ampel zeigt das schlechteste Signal. Neue
// Quellen (z. B. nutzerbezogene Drosselung nach Abo-Soft-Caps) = neuer Provider in STATUS_PROVIDERS,
// kein Umbau der Anzeige.

export type StatusLevel = 'green' | 'yellow' | 'red';

export interface StatusSignal {
    level: StatusLevel;
    // i18n-Key im common-Namespace (status.reason.*), erklaert das Signal im Tooltip
    reasonKey?: string;
}

export interface StatusProvider {
    id: string;
    intervalMs: number;
    fetch: () => Promise<StatusSignal | null>;
}

const LEVEL_RANK: Record<StatusLevel, number> = { green: 0, yellow: 1, red: 2 };

const isLevel = (value: unknown): value is StatusLevel =>
    value === 'green' || value === 'yellow' || value === 'red';

// cProxy GET /health: Erreichbarkeit von cProxy selbst und des KI-Hosts. cProxy selbst verlangt
// keine Auth, der UniversalProxy davor aber schon (401 "Authorization header required", live
// geprueft 2026-09-21) - deshalb fetchWithAuth.
const healthProvider: StatusProvider = {
    id: 'health',
    intervalMs: 60_000,
    fetch: async () => {
        try {
            const res = await fetchWithAuth(`${Config.json.AiServiceProxyUrl}/health`, { cache: 'no-store' });
            // Auth-Probleme (abgelaufener Token o. Ae.) sind kein Plattform-Ausfall -> kein Signal.
            if (res.status === 401 || res.status === 403) return null;
            if (!res.ok) return { level: 'red', reasonKey: 'status.reason.aiUnreachable' };
            const data = await res.json();
            const health = data?.health ?? {};
            const allOk = Object.values(health).length > 0 && Object.values(health).every(v => v === 'ok');
            return allOk ? { level: 'green' } : { level: 'red', reasonKey: 'status.reason.aiUnreachable' };
        } catch {
            return { level: 'red', reasonKey: 'status.reason.aiUnreachable' };
        }
    },
};

// cProxy GET /load (Auth): aggregierte Auslastung, cProxy liefert das Ampel-Level selbst. Auf
// Umgebungen ohne /load (404) oder bei Fehlern kein Signal - fehlende Lastdaten sind kein Ausfall.
const loadProvider: StatusProvider = {
    id: 'load',
    intervalMs: 15_000, // cProxy cached 7 s, empfiehlt 10-15 s
    fetch: async () => {
        try {
            const res = await fetchWithAuth(`${Config.json.AiServiceProxyUrl}/load`, { cache: 'no-store' });
            if (!res.ok) return null;
            const data = await res.json();
            if (!isLevel(data?.level)) return null;
            const reasonKey = data.level === 'red'
                ? 'status.reason.overloaded'
                : data.level === 'yellow' ? 'status.reason.highLoad' : undefined;
            return { level: data.level, reasonKey };
        } catch {
            return null;
        }
    },
};

export const STATUS_PROVIDERS: StatusProvider[] = [healthProvider, loadProvider];

export const aggregateSignals = (signals: (StatusSignal | null)[]): StatusSignal | null => {
    const known = signals.filter((s): s is StatusSignal => s !== null);
    if (known.length === 0) return null;
    return known.reduce((worst, s) => (LEVEL_RANK[s.level] > LEVEL_RANK[worst.level] ? s : worst));
};

// Pollt alle Provider in ihrem eigenen Takt; pausiert, solange der Tab verborgen ist, und fragt beim
// Zurueckkehren sofort neu. Ergebnis: schlechtestes Signal (null = noch nichts bekannt).
export const usePlatformStatus = (providers: StatusProvider[] = STATUS_PROVIDERS): StatusSignal | null => {
    const [signals, setSignals] = useState<Record<string, StatusSignal | null>>({});

    useEffect(() => {
        let cancelled = false;
        const timers: ReturnType<typeof setInterval>[] = [];

        const poll = async (provider: StatusProvider) => {
            // Ohne Login (z. B. Login-Seite) nicht fragen - beide Endpoints sind hinter dem UniversalProxy auth-pflichtig.
            if (document.hidden || !oauthClient.getAccessToken()) return;
            const signal = await provider.fetch();
            if (!cancelled) setSignals(prev => ({ ...prev, [provider.id]: signal }));
        };
        const pollAll = () => providers.forEach(p => void poll(p));

        pollAll();
        providers.forEach(p => timers.push(setInterval(() => void poll(p), p.intervalMs)));
        const onVisibility = () => { if (!document.hidden) pollAll(); };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            cancelled = true;
            timers.forEach(clearInterval);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [providers]);

    return aggregateSignals(Object.values(signals));
};
